import { useEffect, useState, useCallback } from 'react';
import LayOut from '../components/common/Layout';
import Heading from '../components/common/Heading';
import MusicList from '../components/unspokenMusic/MusicList';
import musicMetadata from '../assets/music/metadata.json';
import { ArtData } from '../utils/ArtData';

// A static map of music filename → associated art pieces
const musicArtMap: Record<
  string,
  { id: string; image: string; title: string }[]
> = {};
ArtData.forEach((art) => {
  const musicFile = art.metadata?.music;
  if (musicFile) {
    if (!musicArtMap[musicFile]) musicArtMap[musicFile] = [];
    musicArtMap[musicFile].push({
      id: art.metadata.id,
      image: art.image,
      title: art.metadata.title,
    });
  }
});

interface MusicData {
  id: number;
  filename: string;
  title?: string;
  artist?: string;
  genre: string;
}

export default function Music() {
  const [musicData, setMusicData] = useState<MusicData[]>([]);
  const [currentPlayingId, setCurrentPlayingId] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [expandedGenres, setExpandedGenres] = useState<Set<string>>(new Set());

  useEffect(() => {
    setMusicData(musicMetadata);
  }, []);

  const categorizedMusic = musicData.reduce((acc: any, music) => {
    const { genre } = music;
    if (!acc[genre]) {
      acc[genre] = [];
    }
    acc[genre].push(music);
    return acc;
  }, {});

  const handlePlay = (id: number) => {
    if (currentPlayingId !== null && currentPlayingId !== id) {
      setCurrentPlayingId(null);
      setIsPaused(true);
    }
    setCurrentPlayingId(id);
    setIsPaused(false);
  };

  const handlePause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const toggleGenre = (genre: string) => {
    setExpandedGenres((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(genre)) {
        newSet.delete(genre);
      } else {
        newSet.add(genre);
      }
      return newSet;
    });
  };

  return (
    <LayOut layoutStyle="bg-gradient-to-b from-gray-950 via-gray-900 to-gray-800 text-white">
      {/* Page Header */}
      <div className="text-center pt-24 pb-8 px-4">
        <Heading text="Unspoken Music" />
        <div className="mx-auto mt-3 w-10 h-px bg-custom-stone/50" />
      </div>

      {/* Description */}
      <div className="max-w-3xl mx-auto mb-10 px-2 sm:px-6">
        <p className="text-sm text-gray-400 leading-relaxed">
          A curated collection of publicly available classical recordings.
        </p>
      </div>

      {/* Music grouped by genre */}
      <div className="px-2 sm:px-6 max-w-3xl mx-auto space-y-4">
        {Object.keys(categorizedMusic).map((genre) => {
          const isExpanded = expandedGenres.has(genre);
          const trackCount = categorizedMusic[genre].length;

          return (
            <section
              key={genre}
              className="bg-gray-900/60 rounded-xl border border-gray-800/60 overflow-hidden"
            >
              {/* Genre Header */}
              <button
                onClick={() => toggleGenre(genre)}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-800/40 transition-colors duration-200 cursor-pointer"
              >
                <h2 className="text-sm font-semibold text-white uppercase tracking-widest">
                  {genre}
                </h2>
                <span className="text-xs text-custom-stone/70 font-normal">
                  {trackCount} {trackCount === 1 ? 'track' : 'tracks'}
                </span>
                <div className="flex-1 border-t border-gray-800" />
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${
                    isExpanded ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Track List */}
              {isExpanded && (
                <ul className="px-2 pb-2 border-t border-gray-800/60">
                  {categorizedMusic[genre].map((music: MusicData) => (
                    <li key={music.id}>
                      <MusicList
                        id={music.id}
                        musicFileName={music.filename}
                        title={music.title || 'Untitled Soundtrack'}
                        artist={music.artist || 'Unknown Artist'}
                        isPlaying={currentPlayingId === music.id && !isPaused}
                        isSelected={currentPlayingId === music.id}
                        associatedArt={musicArtMap[music.filename] || []}
                        onPlay={() => handlePlay(music.id)}
                        onPause={handlePause}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </LayOut>
  );
}
